// import Search from "./ModelSearch";
import type { ColumnsType } from "antd/es/table";
import { BookOutlined } from "@ant-design/icons";
import { useSelector, useDispatch } from "react-redux";
import { useEffect, useRef, useState } from "react";
import { translate } from "utils";
import useBreadcrumb from "hooks/useBreadcrumb";
import { APP_PATH } from "constants/path";
import {
	selectPackageSubscriptionFilter,
	setSubscriptionPackageFilter,
} from "store/filter/index.slice";
import {
	PACKAGE_STATUS,
	SubscriptionPackage,
	SubscriptionPackageParams,
} from "./type";
import {
	getListSubscriptionPackageAsync,
	selectSubscriptionPackageList,
	selectSubscriptionPackageTotal,
} from "./redux/subscription-package.slice";
import { Button, Switch, Table } from "antd";
import { uuid } from "short-uuid";
import { Link } from "react-router-dom";
import { formatDateString, formatUSD } from "utils/format";
import { EditOutlined, PlusOutlined, InfoOutlined } from "@ant-design/icons";
import { updateSubscriptionPackageAPI } from "service/apis/subscription-package";
import CheckAccess from "hoc/CheckAccess";
import { PERMISSION } from "../../constants/permission";
import validatePerms from "utils/validate-perms";

// import { PERMISSION } from "constants";

export default function SubscriptionPackages() {
	const dispatch = useDispatch();
	const subscriptionPackages = useSelector(selectSubscriptionPackageList);
	const subscriptionPackageTotal = useSelector(selectSubscriptionPackageTotal);
	// const lang = useSelector(selectLang);
	const { setBreadCrumbs } = useBreadcrumb();
	const formSubscriptionPackageFilter = useSelector(
		selectPackageSubscriptionFilter,
	);
	const [query, setQuery] = useState({
		...formSubscriptionPackageFilter,
	});
	const [isLoading, setIsLoading] = useState(true);
	const canCreated = useRef<boolean>(
		validatePerms([PERMISSION.SUBSCRIPTION.CREATE]),
	);

	const getData = async (query: SubscriptionPackageParams) => {
		const callback = () => {
			setIsLoading(false);
			setQuery({ ...query });
			dispatch(setSubscriptionPackageFilter({ ...query }));
		};
		setIsLoading(true);
		await dispatch(
			getListSubscriptionPackageAsync({
				query: { ...query },
				callback,
			}),
		);
	};
	const handleUpdateStatus = async (id: any, checked: boolean) => {
		const status = checked ? PACKAGE_STATUS.ENABLED : PACKAGE_STATUS.DISABLED;
		await updateSubscriptionPackageAPI(id.toString(), { status });
		return getData(query);
	};
	const handleUpdateBestSeller = async (id: any, isBestSeller: boolean) => {
		await updateSubscriptionPackageAPI(id.toString(), { isBestSeller });
		return getData(query);
	};

	useEffect(() => {
		getData(query);
		setBreadCrumbs([
			{
				label: translate("Subscription package"),
				path: APP_PATH.subscriptionClass,
				icon: <BookOutlined />,
			},
		]);
	}, []);

	const columns: ColumnsType<SubscriptionPackage> = [
		{
			title: translate("NO"),
			dataIndex: "",
			key: "NO",
			width: "50px",
			align: "center",
			render: (_, __, index: number) => (
				<p>{query.skip * query.take + index + 1}</p>
			),
		},
		{
			title: translate("Subscription name"),
			dataIndex: "name",
			key: "name",
			render: (name, record) => {
				return (
					<Link to={`${APP_PATH.subscriptionClass}/${record.id}`}>
						<p>{name}</p>
					</Link>
				);
			},
		},
		{
			title: translate("Price"),
			dataIndex: "prices",
			key: "prices",
			render: (_, record) => `${formatUSD(record.prices?.[0]?.price)} USD`,
		},
		{
			title: translate("Created date"),
			dataIndex: "createdAt",
			key: "createdAt",
			render: value => `${formatDateString(value)}`,
		},
		{
			title: translate("Updated date"),
			dataIndex: "updatedAt",
			key: "updatedAt",
			render: value => `${formatDateString(value)}`,
		},
		{
			title: translate("Best seller"),
			dataIndex: "isBestSeller",
			key: "isBestSeller",
			render: (value, record) => (
				<Switch
					defaultChecked={value}
					checkedChildren={translate("Enable")}
					unCheckedChildren={translate("Disable")}
					onChange={checked => handleUpdateBestSeller(record.id, checked)}
				/>
			),
		},
		{
			title: translate("Status"),
			dataIndex: "status",
			key: "status",
			render: (value, record) => {
				const checked = value === PACKAGE_STATUS.ENABLED;
				return (
					<Switch
						defaultChecked={checked}
						checkedChildren={translate("Enable")}
						unCheckedChildren={translate("Disable")}
						onChange={checked => handleUpdateStatus(record.id, checked)}
					/>
				);
			},
		},
		{
			title: translate("Action"),
			dataIndex: "action",
			key: "action",
			render: (_, record) => (
				<div className="flex justify-center">
					<Link to={`${APP_PATH.subscriptionClass}/${record.id}`}>
						<Button
							type="primary"
							className="m-1 flex items-center justify-center bg-[#1890ff]"
							icon={
								<CheckAccess
									perms={[PERMISSION.SUBSCRIPTION.UPDATE]}
									componentReplace={<InfoOutlined />}
								>
									<EditOutlined />
								</CheckAccess>
							}
							shape="circle"
						/>
					</Link>
				</div>
			),
		},
	];
	const handleChangePage = (page: number) => {
		getData({ ...query, skip: page - 1 });
	};
	const handlePageSizeChange = (_: number, size: number) => {
		getData({ ...query, skip: 0, take: size });
	};

	return (
		<div className="h-full bg-white p-5 my-5 fade-in">
			{canCreated.current && (
				<Link to={APP_PATH.subscriptionClassCreate} className="self-end">
					<Button
						type="primary"
						className="bg-[#84AD2B] hover:bg-[#84AD2B] focus:bg-[#84AD2B] flex items-center rounded border-0"
						icon={<PlusOutlined />}
					>
						{translate("Create")}
					</Button>
				</Link>
			)}

			<div className="mt-10">
				<Table
					dataSource={subscriptionPackages}
					bordered
					scroll={{ x: 800 }}
					columns={columns}
					rowKey={record => record.id || uuid()}
					loading={isLoading}
					pagination={{
						// pageSizeOptions: ["5", "10", "20", "50"],
						total: subscriptionPackageTotal,
						current: query.skip + 1,
						pageSize: query.take,
						showSizeChanger: true,
						showTotal: total =>
							`${translate("Total")} ${total} ${translate("results")}`,
						onChange: handleChangePage,
						onShowSizeChange: handlePageSizeChange,
					}}
					locale={{
						emptyText: (
							<span className="text-gray-8">{translate("No data")}</span>
						),
					}}
				/>
			</div>
		</div>
	);
}
